import {PARTNER_SOURCES} from "@/helpers/partnerSource";

export const FIELD_BOOLEAN = "boolean";
export const FIELD_NUMBER = "number";
export const FIELD_TEXT = "text";
export const FIELD_SELECT = "select";
export const FIELD_COLOR = "color";
export const FIELD_DATETIME = "datetime";
export const FIELD_READONLY = "readonly";
export const FIELD_LINK = "link";

const bool = (field) => ({field: field, type: FIELD_BOOLEAN});
const num = (field) => ({field: field, type: FIELD_NUMBER});
const decimal = (field) => ({field: field, type: FIELD_NUMBER, decimal: true});
const text = (field) => ({field: field, type: FIELD_TEXT});
const superUserText = (field) => ({field: field, type: FIELD_TEXT, superUserOnly: true});
const link = (field) => ({field: field, type: FIELD_LINK});
const select = (field, options) => ({field: field, type: FIELD_SELECT, options: options});
const color = (field) => ({field: field, type: FIELD_COLOR});
const datetime = (field) => ({field: field, type: FIELD_DATETIME});
const readonly = (field) => ({field: field, type: FIELD_READONLY});
const remoteSelect = (field, source) => ({field: field, type: FIELD_SELECT, source: source});

export const BLACKBOX_HIDDEN_SETTINGS = new Set([
    "settingsPage.addItemsToTop",
    "settingsPage.autoCloseAccountOnPaymentEnabled",
    "settingsPage.autoGroupingEnabled",
    "settingsPage.autoPrintShiftXY",
    "settingsPage.autoPrintSummaryOnCashOrderOnlineOrder",
    "settingsPage.autoPrintSummaryTicketEnabled",
    "settingsPage.autoPrintSummaryTicketOnSend",
    "settingsPage.autoPrintVatTicket",
    "settingsPage.hideShiftInfo",
    "settingsPage.kioskAutoCloseAccountOnPaymentEnabled",
    "settingsPage.kitchenDisplayAutoPrintOnStateChange",
    "settingsPage.onlineOrderAutoCloseAccountOnPaymentEnabled",
    "settingsPage.peppolEnabled",
    "settingsPage.pinLockEnabled",
    "settingsPage.printProductsSectionOnZReport",
    "settingsPage.refundEnabled",
    "settingsPage.shiftEnabled",
    "settingsPage.showDefaultOptionsDescription",
    "settingsPage.ticketFooterText"
]);

export const SETTINGS_FIELDS = {
    "SettingsAppPanel": {
        "settingsPage.addItemsToTop": bool("addItemsToTop"),
        "settingsPage.applyKioskBrandColors": bool("applyKioskBrandColors"),
        "settingsPage.autoCloseAccountOnPaymentEnabled": bool("autoCloseAccountOnPaymentEnabled"),
        "settingsPage.autoGroupingEnabled": bool("autoGroupingEnabled"),
        "settingsPage.autoOpenCashRegister": bool("autoOpenCashRegister"),
        "settingsPage.autoOpenNewAccount": bool("autoOpenNewAccount"),
        "settingsPage.hideProductImagesInApp": bool("hideProductImagesInApp"),
        "settingsPage.hideShiftInfo": bool("hideShiftInfo"),
        "settingsPage.kioskAutoCloseAccountOnPaymentEnabled": bool("kioskAutoCloseAccountOnPaymentEnabled"),
        "settingsPage.kioskBackgroundVideoUrl": link("kioskBackgroundVideoUrl"),
        "settingsPage.kioskBuzzerNumberRequired": bool("kioskBuzzerNumberRequired"),
        "settingsPage.kioskOrderActive": bool("kioskOrderActive"),
        "settingsPage.kioskOrderNameRequired": bool("kioskOrderNameRequired"),
        "settingsPage.masterKioskIncludeMasterInPicker": bool("masterKioskIncludeMasterInPicker"),
        "settingsPage.masterKioskOrgEnabled": bool("masterKioskOrgEnabled"),
        "settingsPage.normalAccountOpenButton": bool("normalAccountOpenButton"),
        "settingsPage.orderConfirmationEmailBccEnabled": bool("orderConfirmationEmailBccEnabled"),
        "settingsPage.paymentTypeOfferedEnabled": bool("paymentTypeOfferedEnabled"),
        "settingsPage.paymentTypeTransferEnabled": bool("paymentTypeTransferEnabled"),
        "settingsPage.pinLockEnabled": bool("pinLockEnabled"),
        "settingsPage.pinLockTimeout": num("pinLockTimeout"),
        "settingsPage.promptCoversEnabled": bool("promptCoversEnabled"),
        "settingsPage.refundEnabled": bool("refundEnabled"),
        "settingsPage.removeStockFromParentOrgHint": bool("removeStockFromParentOrg"),
        "settingsPage.sendOrderBehavior": select("sendOrderBehavior", [
            {value: "DEFAULT", label: "settingsPage.DEFAULT"},
            {value: "NAV_BACK", label: "settingsPage.NAV_BACK"},
            {value: "NAV_PAYMENT", label: "settingsPage.NAV_PAYMENT"},
        ]),
        "settingsPage.shiftAutoClose": bool("shiftAutoClose"),
        "settingsPage.shiftAutoOpen": bool("shiftAutoOpen"),
        "settingsPage.shiftCounterEnabled": bool("shiftOrderCounterEnabled"),
        "settingsPage.shiftEnabled": bool("shiftsEnabled"),
        "settingsPage.showDefaultOptionsDescription": bool("showDefaultOptionsDescription"),
        "settingsPage.tableSectionEnabled": bool("tableSectionEnabled"),
        "settingsPage.takeawaySectionEnabled": bool("takeawaySectionEnabled"),
        "settingsPage.uiStateOrderProductGroupDoubleCols": bool("uiStateOrderProductGroupDoubleCols"),
    },
    "SettingsBlackboxPanel": {
        "settingsPage.blackboxBasicAuth": text("blackboxBasicAuth"),
        "settingsPage.blackboxEstablishmentNumber": text("establishmentNumber"),
        "settingsPage.blackboxFdmId": text("blackboxFdm"),
        "settingsPage.fdmPosId": superUserText("fdmPosId"),
        "settingsPage.fdmUrl": text("fdmUrl"),
    },
    "SettingsOrgPanel": {
        "generic.openingHours": link("openingHours"),
        "settingsPage.address": link("address"),
        "settingsPage.bankBic": text("accountBankBic"),
        "settingsPage.bankIban": text("accountBankNumber"),
        "settingsPage.bankName": text("accountBankName"),
        "settingsPage.brandColor": color("brandColor"),
        "settingsPage.brandSecondaryColor": color("brandSecondaryColor"),
        "settingsPage.brandTertiaryColor": color("brandTertiaryColor"),
        "settingsPage.contactEmail": text("contactEmail"),
        "settingsPage.logoImageUrl": link("logoImageUrl"),
        "settingsPage.mainPrinterIpAddress": text("mainPrinterIpAddress"),
        "settingsPage.organisationId": readonly("orgId"),
        "settingsPage.organisationName": text("name"),
        "settingsPage.telephone": text("telephone"),
        "settingsPage.ticketLogoImageUrl": link("ticketLogoImageUrl"),
        "settingsPage.vatNumber": text("vatNumber"),
        "settingsPage.website": text("website"),
    },
    "SettingsPrinterPanel": {
        "settingsPage.autoPrintShiftXY": bool("autoPrintXZOnShiftClose"),
        "settingsPage.autoPrintSummaryOnCashOrderKiosk": bool("kioskAutoPrintSummaryTicketOnCashOrder"),
        "settingsPage.autoPrintSummaryOnCashOrderOnlineOrder": bool("onlineOrderAutoPrintSummaryTicketOnCashOrder"),
        "settingsPage.autoPrintSummaryTicketEnabled": bool("autoPrintSummaryTicketEnabled"),
        "settingsPage.autoPrintSummaryTicketOnSend": bool("autoPrintSummaryTicketOnSend"),
        "settingsPage.autoPrintVatTicket": bool("autoPrintVatTicket"),
        "settingsPage.bigKitchenFontSize": bool("printerSettings.bigKitchenFontSize"),
        "settingsPage.charactersPerLine": num("printerSettings.charactersPerLine"),
        "settingsPage.compressedTicketsEnabled": bool("compressedTicketsEnabled"),
        "settingsPage.digitalVatTicketEnabled": bool("digitalVatTicketEnabled"),
        "settingsPage.kitchenGroupItems": bool("printerSettings.kitchenGroupItems"),
        "settingsPage.kitchenPrintEnabled": bool("kitchenPrintEnabled"),
        "settingsPage.kitchenTopSpacing": num("printerSettings.kitchenTopSpacing"),
        "settingsPage.printPhoneOnTicket": bool("printPhoneOnTicket"),
        "settingsPage.printProductsSectionOnZReport": bool("printProductsSectionOnZReport"),
        "settingsPage.printStatusUrlQrCode": bool("printStatusUrlQrCode"),
        "settingsPage.ticketFooterText": link("ticketFooterText"),
        "settingsPage.ticketHeaderText": link("ticketHeaderText"),
    },
    "SettingsSuperUserPanel": {
        "generic.orgType": select("orgType", [
            {value: "RESTAURANT"},
            {value: "RETAIL"},
            {value: "PUB"},
            {value: "CAFE"},
            {value: "BAR"},
            {value: "HOTEL"},
            {value: "BAKERY"},
            {value: "FAST_FOOD"},
            {value: "FOOD_TRUCK"},
            {value: "ICE_CREAM"},
            {value: "PIZZA"},
            {value: "KEBAB"},
            {value: "SUSHI"},
            {value: "CATERING"},
            {value: "MEDICAL"},
            {value: "OTHER"},
            {value: "FOOD_COURT"},
        ]),
        "settingsPage.autoAssignDeviceToMainType": bool("autoAssignDeviceToMainType"),
        "settingsPage.customWebshopDomain": text("customWebshopDomain"),
        "settingsPage.dataChangeLock": bool("dataChangeLock"),
        "settingsPage.digitalMenuEnabled": bool("digitalMenuEnabled"),
        "settingsPage.includeParentProductsHint": bool("includeParentProducts"),
        "settingsPage.kitchenDisplayAutoPrintOnStateChange": bool("kitchenDisplayAutoPrintOnStateChange"),
        "settingsPage.kitchenDisplayEnabled": bool("kitchenDisplayEnabled"),
        "settingsPage.kitchenDisplayPreparingStateEnabled": bool("kitchenDisplayPreparingStateEnabled"),
        "settingsPage.mapsAutoCompleteEnabled": bool("mapsAutoCompleteEnabled"),
        "settingsPage.onlineOrderWebPageEnabled": bool("onlineOrderWebPageEnabled"),
        "settingsPage.parentOrgId": num("parentOrgId"),
        "settingsPage.partnerSource": select("partnerSource", [
            {value: null, label: "generic.none"},
            ...PARTNER_SOURCES.map(source => ({value: source.value})),
        ]),
        "settingsPage.peppolEnabled": bool("peppolEnabled"),
        "settingsPage.status": select("state", [
            {value: "ACTIVE", label: "settingsPage.active"},
            {value: "BLOCKED", label: "settingsPage.blocked"},
            {value: "BEHIND_PAYMENT", label: "settingsPage.behindPayment"},
            {value: "INACTIVE", label: "settingsPage.inactive"},
        ]),
        "settingsPage.subscriptionEnd": datetime("subscriptionEnd"),
        "settingsPage.supportChatCrispEnabled": bool("supportChatCrispEnabled"),
        "settingsPage.supportVipEnabled": bool("supportVipEnabled"),
        "settingsPage.vwActivationMerchantApiKey": text("vwActivationMerchantApiKey"),
        "settingsPage.vwActivationMerchantApiSecret": text("vwActivationMerchantApiSecret"),
        "settingsPage.vwActivationPin": num("vwActivationPin"),
        "settingsPage.vwEnabled": bool("vwEnabled"),
        "settingsPage.vwIsvAmount": text("vwIsvAmount"),
        "settingsPage.vwTerminalId": text("vwTerminalId"),
        "settingsPage.vwTerminalMerchantId": text("vwTerminalMerchantId"),
        "settingsPage.webOrgaName": text("webOrgId"),
        "settingsPage.workerShiftsEnabled": bool("workerShiftsEnabled"),
    },
    "SettingsVivaPanel": {
        "settingsPage.vwTerminalId": superUserText("vwTerminalId"),
        "settingsPage.vwTerminalMerchantId": superUserText("vwTerminalMerchantId"),
    },
    "SettingsWebshopPanel": {
        "settingsPage.bannerImageUrl": link("bannerImageUrl"),
        "settingsPage.eatInEnabled": bool("onlineOrderEatInEnabled"),
        "settingsPage.hasImageBackground": bool("hasImageBackground"),
        "settingsPage.onlineOrderAutoCloseAccountOnPaymentEnabled": bool("onlineOrderAutoCloseAccountOnPaymentEnabled"),
        "settingsPage.onlineOrderBankTransferEnabled": bool("onlineOrderBankTransferEnabled"),
        "settingsPage.onlineOrderDeliveryEnabled": bool("onlineOrderDeliveryEnabled"),
        "settingsPage.onlineOrderExecutionTimeEatInEnabled": bool("onlineOrderExecutionTimeEatInEnabled"),
        "settingsPage.onlineOrderExecutionTimeInputEnabled": bool("onlineOrderExecutionTimeInputEnabled"),
        "settingsPage.onlineOrderHideNoStockProducts": bool("onlineOrderHideNoStockProducts"),
        "settingsPage.onlineOrderMinAmount": decimal("onlineOrderMinAmount"),
        "settingsPage.onlineOrderMinExecutionDelayMinutes": num("onlineOrderMinExecutionDelayMinutes"),
        "settingsPage.onlineOrderOpen": bool("onlineOrderOpen"),
        "settingsPage.onlineOrderPickupEnabled": bool("onlineOrderPickupEnabled"),
        "settingsPage.onlineOrderPromoCodeInputEnabled": bool("onlineOrderPromoCodeInputEnabled"),
        "settingsPage.onlineOrderVersion": select("onlineOrderVersion", [
            {value: "V1"},
            {value: "V2"},
        ]),
        "settingsPage.payAtPickupEnabled": bool("payAtPickupEnabled"),
        "settingsPage.payOnlineEnabled": bool("payOnlineEnabled"),
    },
};
